import React from 'react';
import { useEventTimeline, useEventTrends, useDetectionUpdates, usePlan } from '@redbax/shared';
import type { TimelineBucket } from '@redbax/shared';
import {
  PageHeader,
  TimelineBar,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  UpgradePrompt,
} from '@redbax/ui-kit';
import { EventHistoryTab } from '../components/event-history-tab';

type TimeRange = '24h' | '48h' | '7d' | '30d';

const RANGE_CONFIG: Record<TimeRange, { label: string; hours: number; granularity: string; trendDays: number }> = {
  '24h': { label: 'Last 24 hours (5 min)', hours: 24, granularity: '5min', trendDays: 1 },
  '48h': { label: 'Last 48 hours (10 min)', hours: 48, granularity: '10min', trendDays: 2 },
  '7d':  { label: 'Last 7 days (1 hour)', hours: 7 * 24, granularity: 'hour', trendDays: 7 },
  '30d': { label: 'Last 30 days (6 hours)', hours: 30 * 24, granularity: '6hour', trendDays: 30 },
};

const INTERVAL_MS: Record<string, number> = {
  '5min': 5 * 60_000,
  '10min': 10 * 60_000,
  'hour': 60 * 60_000,
  '6hour': 6 * 60 * 60_000,
  'day': 24 * 60 * 60_000,
  'week': 7 * 24 * 60 * 60_000,
};

function fillBuckets(
  fromIso: string,
  toIso: string,
  granularity: string,
  data: TimelineBucket[],
): Array<{ timestamp: string; count: number }> {
  const interval = INTERVAL_MS[granularity] ?? INTERVAL_MS['hour'];

  const countByMs = new Map<number, number>();
  for (const b of data) {
    const ms = Math.floor(new Date(b.timestamp).getTime() / interval) * interval;
    countByMs.set(ms, (countByMs.get(ms) ?? 0) + b.count);
  }

  const startMs = Math.floor(new Date(fromIso).getTime() / interval) * interval;
  const endMs = new Date(toIso).getTime();
  const result: Array<{ timestamp: string; count: number }> = [];

  for (let t = startMs; t < endMs; t += interval) {
    result.push({
      timestamp: new Date(t).toISOString(),
      count: countByMs.get(t) ?? 0,
    });
  }

  return result;
}

export function EventsPage() {
  const [range, setRange] = React.useState<TimeRange>('24h');
  const config = RANGE_CONFIG[range];

  const [from, to] = React.useMemo(() => {
    const now = new Date();
    return [
      new Date(now.getTime() - config.hours * 60 * 60 * 1000).toISOString(),
      now.toISOString(),
    ];
  }, [range]);

  const { data: timeline, isLoading: timelineLoading } = useEventTimeline(from, to, config.granularity);
  const { data: trends, isLoading: trendsLoading } = useEventTrends(config.trendDays);

  useDetectionUpdates();

  const { isPersonal, retentionDays } = usePlan();

  // The free tier keeps seven days of events (story #132), so asking for thirty is the moment the
  // boundary becomes visible - and the moment to explain it. Without this the chart simply goes flat
  // before the window starts, which reads as "nothing happened" rather than "we stopped keeping it".
  const beyondRetention = isPersonal && retentionDays !== null && config.trendDays > retentionDays;

  const filledData = React.useMemo(
    () => fillBuckets(from, to, config.granularity, timeline?.buckets ?? []),
    [from, to, config.granularity, timeline],
  );

  return (
    <div>
      <PageHeader
        title="Events"
        description={
          isPersonal && retentionDays !== null
            ? `Detection event timeline and analysis. Free accounts keep ${retentionDays} days of events.`
            : 'Detection event timeline and analysis'
        }
      />

      {beyondRetention && (
        <UpgradePrompt
          className="mb-6"
          surface="retention_boundary"
          title={`Your free account keeps ${retentionDays} days of events`}
          description={
            `There is nothing before that to show for this range. Upgrading to a team keeps your full ` +
            'history, so you can see trends over months rather than days.'
          }
        />
      )}

      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {trends && (
            <div className="mb-6">
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-semibold text-text-primary">
                      {trends.totalEvents.toLocaleString()}
                    </span>
                    <span className="text-sm text-text-secondary">total events</span>
                    {trends.changePercent !== 0 && (
                      <Badge variant={trends.changePercent > 0 ? 'destructive' : 'default'}>
                        {trends.changePercent > 0 ? '+' : ''}
                        {trends.changePercent.toFixed(1)}%
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <TimelineBar
            data={filledData}
            loading={timelineLoading}
            granularity={config.granularity}
            headerAction={
              <Select value={range} onValueChange={(v) => setRange(v as TimeRange)}>
                <SelectTrigger className="w-[260px]">
                  <span>{config.label}</span>
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(RANGE_CONFIG) as [TimeRange, typeof config][]).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      {cfg.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />

          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Category Trends</CardTitle>
              </CardHeader>
              <CardContent>
                {trendsLoading ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-8 animate-pulse rounded bg-surface-raised" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {trends?.categories.map((cat) => (
                      <div key={cat.category} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{cat.category}</Badge>
                          <span className="text-sm text-text-primary">
                            {cat.current.toLocaleString()} detections
                          </span>
                        </div>
                        <span className="text-xs text-text-secondary">
                          {cat.changePercent > 0 ? '+' : ''}
                          {cat.changePercent.toFixed(1)}% vs previous period
                        </span>
                      </div>
                    ))}
                    {(!trends?.categories || trends.categories.length === 0) && (
                      <p className="text-sm text-text-secondary">No trend data available</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <EventHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
